/**
 * useHermesActionExecutor — Executes Skerpa action cards.
 *
 * Write strategy (ADR-001 migration period):
 * - Competitors/Personas: write to PostgreSQL (their UIs read from PG)
 * - Everything else: write to SPARQL via unified entityCrudApi
 *
 * After mutation, emits typed entity events. Views subscribe to refresh themselves.
 * No dual-writes — each entity type has ONE write path.
 */

import { useState, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useProjectStore } from '@/stores/projectStore'
import { useAuthStore } from '@/stores/authStore'
import { useSkerpaContextStore } from '@/stores/skerpaContextStore'
import { entityCrudApi } from '@/lib/api/entityCrudApi'
import { artifactsApi, competitorMapApi, personaWorkshopApi, valueDesignApi, businessModelApi } from '@/lib/api/client'
import { update as sparqlUpdate } from '@/lib/api/sparql-adapter'
import { ALL_CLASSES } from '@/lib/ontology/schema'
import { NAMESPACES } from '@/lib/ontology/namespaces'
import { entityEvents } from '@/lib/entityEventBus'
import type { SkerpaAction, BMCBlock } from '@ispot/shared'

// ── Update routing ──
// Fields handled by entityCrudApi (writes to standard rdf:label / io:description /
// io:status / io:confidence triples). Anything else is treated as an arbitrary
// property and written directly via SPARQL.
const CANONICAL_UPDATE_FIELDS = new Set(['label', 'description', 'status', 'confidence'])

/** Resolve an update key (e.g. "estimated_size") to a property URI on the
 *  given entity type, using the ontology schema. Falls back to the io:
 *  namespace if no schema match — Skerpa's prompts often emit camelCase /
 *  snake_case names that round-trip cleanly via that namespace. */
function resolvePropertyUri(entityType: string, key: string): string {
  const candidates = key.toLowerCase().replace(/[_\s-]/g, '')
  for (const cls of ALL_CLASSES) {
    if (!cls.uri.endsWith(`#${entityType}`) && cls.localName !== entityType) continue
    for (const p of cls.properties) {
      if (p.type !== 'data') continue
      if (p.localName.toLowerCase() === candidates) return p.uri
      if (p.localName.toLowerCase().replace(/has/, '') === candidates) return p.uri
      if (p.label.toLowerCase().replace(/[^a-z0-9]/g, '') === candidates) return p.uri
    }
  }
  // Fallback: io: namespace + camelCase of the key
  const camel = key
    .toLowerCase()
    .replace(/[_\s-]+(.)/g, (_, c: string) => c.toUpperCase())
  return `${NAMESPACES.io}${camel}`
}

function escapeSparql(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n').replace(/\r/g, '\\r')
}

/** Upsert a property triple: replace any existing values for that predicate
 *  with the new value. URI-shaped values are written as IRIs.
 *
 *  Implementation note: a single `DELETE { … } INSERT { … } WHERE { OPTIONAL { … } }`
 *  fails in Jena because OPTIONAL must attach to a preceding pattern; a
 *  WHERE clause that's *only* OPTIONAL is a syntax error. Two top-level
 *  Update operations joined with `;` give us upsert semantics that
 *  succeed whether or not a prior value exists. */
async function upsertPropertyTriple(instanceUri: string, propertyUri: string, value: string): Promise<void> {
  const isIri = /^https?:\/\//.test(value)
  const formatted = isIri ? `<${value}>` : `"${escapeSparql(value)}"`
  await sparqlUpdate(`
    DELETE WHERE { <${instanceUri}> <${propertyUri}> ?old } ;
    INSERT DATA { <${instanceUri}> <${propertyUri}> ${formatted} }
  `)
}

const PERSONA_JTBD_TYPES = new Set(['customerjob', 'customerpain', 'customergain'])
const VPC_ENTITY_TYPES = new Set(['painreliever', 'gaincreator'])

const CANVAS_SECTION_ALIASES: Record<string, string> = {
  who: 'customers',
  impact: 'quantifiable-impact',
  current: 'alternatives',
  root: 'problem',
  evidence: 'context',
  shortcomings: 'alternative-shortcomings',
  'alternative-shortcomings': 'alternative-shortcomings',
  'alternative shortcomings': 'alternative-shortcomings',
}

const CANVAS_SECTION_ENTITY_TYPES: Record<string, string[]> = {
  context: ['Problem'],
  problem: ['Problem'],
  alternatives: ['Alternative', 'Competitor'],
  customers: ['CustomerSegment', 'Persona', 'Person'],
  'emotional-impact': ['CustomerPain'],
  'quantifiable-impact': ['CustomerPain'],
  'alternative-shortcomings': ['AlternativeDisadvantage'],
  'customer-jobs': ['CustomerJob'],
  'customer-gains': ['CustomerGain'],
  'customer-pains': ['CustomerPain'],
  'value-propositions': ['ValueProposition'],
  'products-services': ['Product', 'Feature'],
  'pain-relievers': ['PainReliever'],
  'gain-creators': ['GainCreator'],
  'key-partners': ['KeyPartnership'],
  'key-activities': ['KeyActivity'],
  'key-resources': ['KeyResource'],
  'customer-relationships': ['CustomerRelationship'],
  channels: ['Channel'],
  'customer-segments': ['CustomerSegment'],
  'cost-structure': ['CostStructure'],
  'revenue-streams': ['RevenueStream'],
}

const CANVAS_ENTITY_CANONICAL: Record<string, string> = {
  problem: 'Problem',
  customerpain: 'CustomerPain',
  customer_pain: 'CustomerPain',
  pain: 'CustomerPain',
  customergain: 'CustomerGain',
  customer_gain: 'CustomerGain',
  customerjob: 'CustomerJob',
  customer_job: 'CustomerJob',
  customersegment: 'CustomerSegment',
  customer_segment: 'CustomerSegment',
  persona: 'Persona',
  person: 'Person',
  alternative: 'Alternative',
  competitor: 'Competitor',
  alternativedisadvantage: 'AlternativeDisadvantage',
  alternative_disadvantage: 'AlternativeDisadvantage',
  shortcoming: 'AlternativeDisadvantage',
  alternativeshortcoming: 'AlternativeDisadvantage',
  alternative_shortcoming: 'AlternativeDisadvantage',
  painreliever: 'PainReliever',
  pain_reliever: 'PainReliever',
  gaincreator: 'GainCreator',
  gain_creator: 'GainCreator',
  product: 'Product',
  feature: 'Feature',
  valueproposition: 'ValueProposition',
  value_proposition: 'ValueProposition',
  keypartnership: 'KeyPartnership',
  key_partnership: 'KeyPartnership',
  keyactivity: 'KeyActivity',
  key_activity: 'KeyActivity',
  keyresource: 'KeyResource',
  key_resource: 'KeyResource',
  customerrelationship: 'CustomerRelationship',
  customer_relationship: 'CustomerRelationship',
  channel: 'Channel',
  coststructure: 'CostStructure',
  cost_structure: 'CostStructure',
  revenuestream: 'RevenueStream',
  revenue_stream: 'RevenueStream',
}

/** Maps SPARQL entity type (lowercased) → BMC block key */
const BMC_ENTITY_MAP: Record<string, BMCBlock> = {
  keypartnership: 'keyPartners',
  keyactivity: 'keyActivities',
  keyresource: 'keyResources',
  valueproposition: 'valuePropositions',
  customerrelationship: 'customerRelationships',
  channel: 'channels',
  customersegment: 'customerSegments',
  coststructure: 'costStructure',
  revenuestream: 'revenueStreams',
}

export function useHermesActionExecutor(onViewChange?: (view: string) => void) {
  const [executedActions, setExecutedActions] = useState<Set<string>>(new Set())
  const [executingAction, setExecutingAction] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const currentProject = useProjectStore(s => s.currentProject)
  const account = useAuthStore(s => s.currentAccount)
  const activeResourceId = useSkerpaContextStore(s => s.activeResourceId)
  const activeView = useSkerpaContextStore(s => s.activeView)
  const setPendingOpenSession = useSkerpaContextStore(s => s.setPendingOpenSession)

  const markDone = (key: string) => setExecutedActions(prev => new Set([...prev, key]))
  const invalidate = async () => { await queryClient.invalidateQueries(); queryClient.refetchQueries({ type: 'active' }) }

  const executeAction = useCallback(async (action: SkerpaAction, actionKey: string) => {
    if (executedActions.has(actionKey) || executingAction) return
    if (!currentProject || !account) return
    setExecutingAction(actionKey)
    setActionError(null)

    const accountId = account.id
    const projectId = currentProject.id

    try {
      switch (action.action) {
        case 'add_entity': {
          const typeLower = action.entityType.toLowerCase()
          // Competitor and Persona have dedicated write paths — never route them
          // through the canvas artifact API regardless of which view is active.
          const hasDedicatedPath = typeLower === 'competitor' || typeLower === 'persona' || PERSONA_JTBD_TYPES.has(typeLower)
          const syncedToActiveCanvas = hasDedicatedPath
            ? false
            : await maybeSyncToActiveCanvas(
                action,
                accountId,
                projectId,
                activeView,
                activeResourceId,
              )

          // Route to the correct write path
          if (syncedToActiveCanvas) {
            // Canvas actions must create/link through the artifact API because
            // the visible canvas reads artifact entity links, not raw graph rows.
          } else if (typeLower === 'competitor') {
            await competitorMapApi.createCompetitor(accountId, projectId, {
              name: action.label, whatTheyDo: action.description || '',
              category: action.section || undefined, type: 'direct',
            })
          } else if (typeLower === 'persona') {
            // If we have a section field (photo, demographics, bio, goals, etc.)
            // and an active persona session, update the property — don't create new
            const sectionKey = action.section?.toLowerCase()
            const personaSectionFields = new Set(['photo', 'demographics', 'bio', 'goals', 'pains', 'behaviors', 'quotes'])

            if (sectionKey && personaSectionFields.has(sectionKey) && activeResourceId) {
              // Update existing persona section property
              const session = await personaWorkshopApi.getSession(accountId, projectId, activeResourceId) as {
                personaData: Record<string, unknown>
              }
              const data = session.personaData || {}
              const sectionUpdates: Record<string, unknown> = {}

              if (sectionKey === 'photo') {
                sectionUpdates.name = action.label
                if (action.description) sectionUpdates.role = action.description
              } else if (sectionKey === 'demographics') {
                sectionUpdates.demographics = action.label
                if (action.description) sectionUpdates.context = action.description
              } else if (sectionKey === 'bio') {
                sectionUpdates.bio = action.label
              } else if (sectionKey === 'goals') {
                sectionUpdates.goals = [action.label]
              } else {
                sectionUpdates[sectionKey] = action.label
              }

              await personaWorkshopApi.updateSession(accountId, projectId, activeResourceId, {
                personaData: { ...data, ...sectionUpdates },
              })
            } else {
              // No section or no active session — create a new persona and navigate to it
              const session = await personaWorkshopApi.createSession(accountId, projectId, {
                personaType: 'user', mode: 'deep',
              }) as { id: string }
              await personaWorkshopApi.updateSession(accountId, projectId, session.id, {
                personaData: { name: action.label, role: action.description || '' },
              })
              setPendingOpenSession(session.id)
              if (onViewChange) onViewChange('persona-workshop')
            }
          } else {
            await entityCrudApi.create(accountId, projectId, {
              entityType: action.entityType, label: action.label,
              description: action.description, confidence: 70,
            }, 'skerpa')
          }

          // Persona JTBD sync: write to the correct persona's session JSONB
          if (!syncedToActiveCanvas && PERSONA_JTBD_TYPES.has(typeLower)) {
            const targetSessionId = await resolvePersonaSession(action.description, accountId, projectId, activeResourceId)
            if (targetSessionId) {
              await syncToPersonaSession(typeLower, action.label, action.description, accountId, projectId, targetSessionId, action.section)
            }
          }

          // BMC sync: write BMC entity types to the active business model canvas
          const bmcBlock = BMC_ENTITY_MAP[typeLower]
          if (!syncedToActiveCanvas && bmcBlock) {
            try {
              await syncToBmc(bmcBlock, action.label, accountId, projectId)
            } catch (e) {
              // Log but don't block — entity was created in SPARQL already
              // eslint-disable-next-line no-console
              console.warn('[Skerpa] BMC sync failed:', e)
            }
          }

          // VPC sync: write pain relievers/gain creators to the active VPC canvas
          if (!syncedToActiveCanvas && VPC_ENTITY_TYPES.has(typeLower)) {
            if (activeResourceId && activeView === 'value-design') {
              // VPC is open — write to the active canvas
              try {
                await syncToVpc(typeLower, action.label, accountId, projectId, activeResourceId)
              } catch (e) {
                // Log but don't block — entity was created in SPARQL already
                // eslint-disable-next-line no-console
                console.warn('[Skerpa] VPC sync failed:', e)
              }
            } else {
              // Not on VPC view — find most recent VPC for this project and write there
              try {
                const vpcs = await valueDesignApi.list(accountId, projectId)
                if (vpcs.length > 0) {
                  const targetVpc = vpcs[0] // Most recent
                  await syncToVpc(typeLower, action.label, accountId, projectId, targetVpc.id)
                }
              } catch (e) {
                console.warn('[Skerpa] VPC auto-sync failed:', e)
              }
            }
          }

          entityEvents.emit('entity-created', { entityType: action.entityType, label: action.label, source: 'skerpa' })
          markDone(actionKey)
          await invalidate()
          break
        }

        case 'update_entity': {
          const updateType = action.entityType.toLowerCase()
          if (updateType === 'competitor') {
            const competitors = await competitorMapApi.listCompetitors(accountId, projectId) as Array<{ id: string; name: string }>
            const match = competitors.find(c => c.name.toLowerCase() === action.name.toLowerCase())
            if (!match) throw new Error(`Could not find competitor "${action.name}" to update`)
            await competitorMapApi.updateCompetitor(accountId, projectId, match.id, action.updates)
          } else if (updateType === 'persona') {
            // Persona updates go to personaData JSONB
            const sessions = await personaWorkshopApi.listSessions(accountId, projectId) as Array<{ id: string; personaData: Record<string, unknown> }>
            const match = sessions.find(s => {
              const name = (s.personaData?.name as string) || ''
              return name.toLowerCase() === action.name.toLowerCase()
            })
            if (!match) throw new Error(`Could not find persona "${action.name}" to update`)
            // Map update fields to personaData fields
            const personaUpdates: Record<string, unknown> = {}
            for (const [key, value] of Object.entries(action.updates)) {
              // Support common field names: description→role, name, role, archetype, context
              if (key === 'description' || key === 'role') personaUpdates.role = value
              else if (key === 'name') personaUpdates.name = value
              else if (key === 'archetype') personaUpdates.archetype = value
              else if (key === 'context') personaUpdates.context = value
              else personaUpdates[key] = value
            }
            await personaWorkshopApi.updateSession(accountId, projectId, match.id, {
              personaData: { ...match.personaData, ...personaUpdates },
            })
          } else if (PERSONA_JTBD_TYPES.has(updateType)) {
            const targetSessionId = activeResourceId ?? await resolvePersonaSession(undefined, accountId, projectId, null)
            if (!targetSessionId) throw new Error('No active persona session — open a persona first')
            await updatePersonaJtbdItem(updateType, action.name, action.updates, accountId, projectId, targetSessionId)
          } else {
            const { items } = await entityCrudApi.list(accountId, projectId, action.entityType, { search: action.name })
            const match = items.find(e => e.label.toLowerCase() === action.name.toLowerCase())
            if (!match) throw new Error(`Could not find ${action.entityType} "${action.name}" to update`)

            // Split updates: canonical fields go through entityCrudApi (writes
            // standard rdf:label / io:description / io:status / io:confidence
            // triples); arbitrary properties (tags, profile, location, etc.)
            // are written directly via SPARQL because the EntityCrudApi
            // contract only accepts the canonical four fields.
            const canonicalUpdates: Record<string, unknown> = {}
            const propertyUpdates: Array<[string, string]> = []
            for (const [key, value] of Object.entries(action.updates)) {
              if (CANONICAL_UPDATE_FIELDS.has(key)) {
                canonicalUpdates[key] = value
              } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
                propertyUpdates.push([key, String(value)])
              } else if (Array.isArray(value)) {
                // Array values: write each element on the same predicate
                for (const v of value) propertyUpdates.push([key, String(v)])
              }
            }

            if (Object.keys(canonicalUpdates).length > 0) {
              await entityCrudApi.update(accountId, projectId, action.entityType, match.id, canonicalUpdates, 'skerpa')
            }

            if (propertyUpdates.length > 0) {
              // Group by predicate so the first value upserts (replaces any
              // existing) and subsequent values are appended.
              const seen = new Set<string>()
              for (const [key, value] of propertyUpdates) {
                const propUri = resolvePropertyUri(action.entityType, key)
                if (seen.has(propUri)) {
                  // Append additional values for this predicate
                  const isIri = /^https?:\/\//.test(value)
                  const formatted = isIri ? `<${value}>` : `"${escapeSparql(value)}"`
                  await sparqlUpdate(`INSERT DATA { <${match.id}> <${propUri}> ${formatted} . }`)
                } else {
                  await upsertPropertyTriple(match.id, propUri, value)
                  seen.add(propUri)
                }
              }
            }
          }
          entityEvents.emit('entity-updated', { entityType: action.entityType, label: action.name, source: 'skerpa' })
          markDone(actionKey)
          await invalidate()
          break
        }

        case 'batch_update': {
          const missing: string[] = []
          if (action.entityType.toLowerCase() === 'competitor') {
            const competitors = await competitorMapApi.listCompetitors(accountId, projectId) as Array<{ id: string; name: string }>
            const nameToId = new Map(competitors.map(c => [c.name.toLowerCase(), c.id]))
            const updates: Array<{ id: string; item: { name: string; updates: Record<string, string> } }> = []
            for (const item of action.items) {
              const id = nameToId.get(item.name.toLowerCase())
              if (!id) {
                missing.push(item.name)
                continue
              }
              updates.push({ id, item })
            }
            if (missing.length === 0) {
              for (const { id, item } of updates) await competitorMapApi.updateCompetitor(accountId, projectId, id, item.updates)
            }
          } else {
            const { items } = await entityCrudApi.list(accountId, projectId, action.entityType)
            const nameToUri = new Map(items.map(e => [e.label.toLowerCase(), e.id]))
            const updates: Array<{ uri: string; item: { name: string; updates: Record<string, string> } }> = []
            for (const item of action.items) {
              const uri = nameToUri.get(item.name.toLowerCase())
              if (!uri) {
                missing.push(item.name)
                continue
              }
              updates.push({ uri, item })
            }
            if (missing.length === 0) {
              for (const { uri, item } of updates) {
                // Same split as the single update_entity path: canonical
                // fields via entityCrudApi, arbitrary properties via SPARQL.
                const canonicalUpdates: Record<string, unknown> = {}
                const propertyUpdates: Array<[string, string]> = []
                for (const [key, value] of Object.entries(item.updates)) {
                  if (CANONICAL_UPDATE_FIELDS.has(key)) canonicalUpdates[key] = value
                  else propertyUpdates.push([key, String(value)])
                }
                if (Object.keys(canonicalUpdates).length > 0) {
                  await entityCrudApi.update(accountId, projectId, action.entityType, uri, canonicalUpdates, 'skerpa')
                }
                const seen = new Set<string>()
                for (const [key, value] of propertyUpdates) {
                  const propUri = resolvePropertyUri(action.entityType, key)
                  if (seen.has(propUri)) {
                    const isIri = /^https?:\/\//.test(value)
                    const formatted = isIri ? `<${value}>` : `"${escapeSparql(value)}"`
                    await sparqlUpdate(`INSERT DATA { <${uri}> <${propUri}> ${formatted} . }`)
                  } else {
                    await upsertPropertyTriple(uri, propUri, value)
                    seen.add(propUri)
                  }
                }
              }
            }
          }
          if (missing.length > 0) {
            throw new Error(`Could not find ${missing.length} ${action.entityType} item${missing.length === 1 ? '' : 's'}: ${missing.join(', ')}`)
          }
          entityEvents.emit('entity-updated', { entityType: action.entityType, source: 'skerpa' })
          markDone(actionKey)
          await invalidate()
          break
        }

        case 'delete_entity': {
          if (action.entityType.toLowerCase() === 'competitor') {
            const competitors = await competitorMapApi.listCompetitors(accountId, projectId) as Array<{ id: string; name: string }>
            const match = competitors.find(c => c.name.toLowerCase() === action.name.toLowerCase())
            if (!match) throw new Error(`Could not find competitor "${action.name}" to delete`)
            await competitorMapApi.deleteCompetitor(accountId, projectId, match.id)
          } else if (PERSONA_JTBD_TYPES.has(action.entityType.toLowerCase())) {
            const targetSessionId = activeResourceId
            if (!targetSessionId) throw new Error('No active persona session — open a persona first')
            await deletePersonaJtbdItem(action.entityType.toLowerCase(), action.name, accountId, projectId, targetSessionId)
          } else {
            const { items } = await entityCrudApi.list(accountId, projectId, action.entityType, { search: action.name })
            const match = items.find(e => e.label.toLowerCase() === action.name.toLowerCase())
            if (!match) throw new Error(`Could not find ${action.entityType} "${action.name}" to delete`)
            await entityCrudApi.delete(accountId, projectId, action.entityType, match.id, 'skerpa')
          }
          entityEvents.emit('entity-deleted', { entityType: action.entityType, label: action.name, source: 'skerpa' })
          markDone(actionKey)
          await invalidate()
          break
        }

        case 'link_entities': {
          const [sourceResult, targetResult] = await Promise.all([
            entityCrudApi.list(accountId, projectId, action.source.entityType, { search: action.source.name }),
            entityCrudApi.list(accountId, projectId, action.target.entityType, { search: action.target.name }),
          ])
          const sourceEntity = sourceResult.items.find(e => e.label.toLowerCase() === action.source.name.toLowerCase())
          const targetEntity = targetResult.items.find(e => e.label.toLowerCase() === action.target.name.toLowerCase())
          if (!sourceEntity) throw new Error(`Could not find ${action.source.entityType} "${action.source.name}" to link`)
          if (!targetEntity) throw new Error(`Could not find ${action.target.entityType} "${action.target.name}" to link`)
          await entityCrudApi.createRelation(accountId, projectId, {
            sourceUri: sourceEntity.id, sourceType: action.source.entityType,
            targetUri: targetEntity.id, targetType: action.target.entityType,
            predicate: action.relation,
          }, 'skerpa')
          markDone(actionKey)
          await invalidate()
          break
        }

        case 'navigate': {
          if (onViewChange) onViewChange(action.target)
          break
        }
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Action failed')
    } finally {
      setExecutingAction(null)
    }
  }, [currentProject, account, queryClient, onViewChange, executedActions, executingAction, activeResourceId, activeView, setPendingOpenSession])

  return { executedActions, executingAction, actionError, executeAction }
}

// ── Generic artifact canvas sync ──

function normalizeCanvasSection(section: string | undefined): string | null {
  if (!section) return null
  const key = section.trim().toLowerCase()
  return CANVAS_SECTION_ALIASES[key] || key
}

function canonicalEntityType(entityType: string): string {
  const compact = entityType.replace(/[\s-]/g, '').toLowerCase()
  const snake = entityType.replace(/[\s-]/g, '_').toLowerCase()
  return CANVAS_ENTITY_CANONICAL[compact] || CANVAS_ENTITY_CANONICAL[snake] || entityType
}

function entityTypeForCanvasSection(entityType: string, sectionId: string): string {
  const allowedTypes = CANVAS_SECTION_ENTITY_TYPES[sectionId]
  const canonical = canonicalEntityType(entityType)
  if (!allowedTypes?.length) return canonical
  if (allowedTypes.includes(canonical)) return canonical
  return allowedTypes[0]
}

async function maybeSyncToActiveCanvas(
  action: Extract<SkerpaAction, { action: 'add_entity' }>,
  accountId: string,
  projectId: string,
  activeView: string,
  activeResourceId: string | null,
): Promise<boolean> {
  const sectionId = normalizeCanvasSection(action.section)
  if (!(activeView === 'canvas' || activeView.startsWith('canvas-')) || !activeResourceId || !sectionId) return false

  const entityType = entityTypeForCanvasSection(action.entityType, sectionId)
  const { links } = await artifactsApi.getEntityLinks(accountId, projectId, activeResourceId, sectionId)
  const maxSortOrder = links.reduce((max, link) => Math.max(max, link.sortOrder), -1)

  await artifactsApi.createAndLinkEntity(accountId, projectId, activeResourceId, {
    sectionId,
    label: action.label,
    entityType,
    description: action.description,
    note: action.description,
    sortOrder: maxSortOrder + 1,
    source: 'ai',
    confidence: 70,
  })

  return true
}

// ── Persona resolution ──

/**
 * Resolve which persona session to write to.
 * Extracts persona name from action description (e.g., "Benny pain — ..."),
 * searches all sessions, returns the matching session ID.
 * Falls back to activeResourceId if no name match found.
 */
async function resolvePersonaSession(
  description: string | undefined,
  accountId: string,
  projectId: string,
  fallbackSessionId: string | null,
): Promise<string | null> {
  // Try to extract persona name from description
  // Common patterns: "Benny pain — ...", "Benny's core JTBD", "Benny gain — ..."
  if (description) {
    const sessions = await personaWorkshopApi.listSessions(accountId, projectId) as Array<{ id: string; personaData: { name?: string } }>
    const descLower = description.toLowerCase()

    for (const s of sessions) {
      const name = s.personaData?.name
      if (name && descLower.includes(name.toLowerCase())) {
        return s.id
      }
    }

    // Also try matching first word of description against persona names
    const firstWord = description.split(/[\s,—\-]/)[0]?.trim()
    if (firstWord && firstWord.length > 2) {
      for (const s of sessions) {
        const name = s.personaData?.name
        if (name && name.toLowerCase().startsWith(firstWord.toLowerCase())) {
          return s.id
        }
      }
    }
  }

  return fallbackSessionId
}

// ── Persona JTBD sync ──

type JtbdEntry = {
  id: string
  title: string
  functional: string
  pains: Array<{ id: string; description: string; severity?: string; evidenceCount: number }>
  gains: Array<{ id: string; description: string; importance?: string; evidenceCount: number }>
}
type PersonaSessionData = { jtbds?: JtbdEntry[]; [key: string]: unknown }

async function getPersonaJtbds(accountId: string, projectId: string, sessionId: string): Promise<{ data: PersonaSessionData; jtbds: JtbdEntry[] }> {
  const session = await personaWorkshopApi.getSession(accountId, projectId, sessionId) as { personaData: PersonaSessionData }
  const data = (session.personaData || {}) as PersonaSessionData
  return { data, jtbds: [...(data.jtbds || [])] }
}

async function syncToPersonaSession(
  entityType: string, label: string, description: string | undefined,
  accountId: string, projectId: string, sessionId: string,
  targetJtbdTitle?: string,
) {
  const { data, jtbds } = await getPersonaJtbds(accountId, projectId, sessionId)
  const id = `${entityType}-${Date.now()}`

  if (entityType === 'customerjob') {
    jtbds.push({ id, title: label, functional: description || label, pains: [], gains: [] })
  } else if (entityType === 'customerpain' || entityType === 'customergain') {
    if (jtbds.length === 0) jtbds.push({ id: `jtbd-${Date.now()}`, title: 'General', functional: 'General job', pains: [], gains: [] })
    const target = (targetJtbdTitle && jtbds.find(j => j.title.toLowerCase().includes(targetJtbdTitle.toLowerCase()))) || jtbds[0]
    if (entityType === 'customerpain') target.pains.push({ id, description: label, severity: 'medium', evidenceCount: 0 })
    else target.gains.push({ id, description: label, importance: 'expected', evidenceCount: 0 })
  }

  await personaWorkshopApi.updateSession(accountId, projectId, sessionId, { personaData: { ...data, jtbds } })
}

async function updatePersonaJtbdItem(
  entityType: string, name: string, updates: Record<string, string>,
  accountId: string, projectId: string, sessionId: string,
) {
  const { data, jtbds } = await getPersonaJtbds(accountId, projectId, sessionId)
  const nameLower = name.toLowerCase()

  if (entityType === 'customerjob') {
    const jtbd = jtbds.find(j => j.title.toLowerCase() === nameLower || j.functional.toLowerCase() === nameLower)
    if (!jtbd) throw new Error(`Could not find CustomerJob "${name}"`)
    if (updates.title) jtbd.title = updates.title
    if (updates.functional) jtbd.functional = updates.functional
    if (updates.emotional) (jtbd as Record<string, unknown>).emotional = updates.emotional
    if (updates.social) (jtbd as Record<string, unknown>).social = updates.social
  } else if (entityType === 'customerpain') {
    let found = false
    for (const jtbd of jtbds) {
      const pain = jtbd.pains.find(p => p.description.toLowerCase() === nameLower)
      if (pain) {
        if (updates.description) pain.description = updates.description
        if (updates.severity) pain.severity = updates.severity
        found = true; break
      }
    }
    if (!found) throw new Error(`Could not find CustomerPain "${name}"`)
  } else if (entityType === 'customergain') {
    let found = false
    for (const jtbd of jtbds) {
      const gain = jtbd.gains.find(g => g.description.toLowerCase() === nameLower)
      if (gain) {
        if (updates.description) gain.description = updates.description
        if (updates.importance) gain.importance = updates.importance
        found = true; break
      }
    }
    if (!found) throw new Error(`Could not find CustomerGain "${name}"`)
  }

  await personaWorkshopApi.updateSession(accountId, projectId, sessionId, { personaData: { ...data, jtbds } })
}

async function deletePersonaJtbdItem(
  entityType: string, name: string,
  accountId: string, projectId: string, sessionId: string,
) {
  const { data, jtbds } = await getPersonaJtbds(accountId, projectId, sessionId)
  const nameLower = name.toLowerCase()

  if (entityType === 'customerjob') {
    const before = jtbds.length
    const filtered = jtbds.filter(j => j.title.toLowerCase() !== nameLower && j.functional.toLowerCase() !== nameLower)
    if (filtered.length === before) throw new Error(`Could not find CustomerJob "${name}"`)
    await personaWorkshopApi.updateSession(accountId, projectId, sessionId, { personaData: { ...data, jtbds: filtered } })
    return
  }

  let found = false
  for (const jtbd of jtbds) {
    if (entityType === 'customerpain') {
      const before = jtbd.pains.length
      jtbd.pains = jtbd.pains.filter(p => p.description.toLowerCase() !== nameLower)
      if (jtbd.pains.length < before) { found = true; break }
    } else if (entityType === 'customergain') {
      const before = jtbd.gains.length
      jtbd.gains = jtbd.gains.filter(g => g.description.toLowerCase() !== nameLower)
      if (jtbd.gains.length < before) { found = true; break }
    }
  }
  if (!found) throw new Error(`Could not find ${entityType === 'customerpain' ? 'CustomerPain' : 'CustomerGain'} "${name}"`)

  await personaWorkshopApi.updateSession(accountId, projectId, sessionId, { personaData: { ...data, jtbds } })
}

// ── BMC sync ──

async function syncToBmc(
  blockKey: BMCBlock, label: string,
  accountId: string, projectId: string,
) {
  // Ensure a default canvas exists, then append the item to the correct block
  const canvas = await businessModelApi.ensureDefault(accountId, projectId)
  if (!canvas?.id) return

  const emptyBlock = { items: [] as string[] }
  const data = {
    keyPartners: emptyBlock, keyActivities: emptyBlock, keyResources: emptyBlock,
    valuePropositions: emptyBlock, customerRelationships: emptyBlock, channels: emptyBlock,
    customerSegments: emptyBlock, costStructure: emptyBlock, revenueStreams: emptyBlock,
    ...(canvas.data || {}),
  }
  const block = data[blockKey] || emptyBlock

  // Avoid duplicates
  if (block.items.includes(label)) return

  await businessModelApi.updateCanvas(accountId, projectId, canvas.id, {
    data: { ...data, [blockKey]: { items: [...block.items, label] } },
  })
}

// ── VPC sync ──

async function syncToVpc(
  entityType: string, label: string,
  accountId: string, projectId: string, vpcId: string,
) {
  const vpc = await valueDesignApi.get(accountId, projectId, vpcId) as {
    data: {
      painRelievers?: Array<{ id?: string; description: string; painId: string; painIds?: string[] }>
      gainCreators?: Array<{ id?: string; description: string; gainId: string; gainIds?: string[] }>
    }
  }
  const vpcData = vpc.data || {}
  const id = `skerpa-${Date.now()}`

  if (entityType === 'painreliever') {
    const painRelievers = [...(vpcData.painRelievers || []), { id, description: label, painId: 'unlinked', painIds: [] }]
    await valueDesignApi.update(accountId, projectId, vpcId, { data: { painRelievers } })
  } else if (entityType === 'gaincreator') {
    const gainCreators = [...(vpcData.gainCreators || []), { id, description: label, gainId: 'unlinked', gainIds: [] }]
    await valueDesignApi.update(accountId, projectId, vpcId, { data: { gainCreators } })
  }
}
